import { useTranslations } from 'next-intl';
import { checkEnableCreateElement } from '@/app/apis';
import { NEW_MEMBERSHIP_REFERENCE_LIMIT, REFERENCE_RIGHT_LIST } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { getFeedBackUrl, isUltimateOrHigherPlan, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useElementErrorMsg } from './use-element-error-msg';
import { useFetch } from './use-fetch';
import { useGlobalModal } from './use-global-modal';
import { useMembershipRevision } from './use-membership-revision';
import { useQuota } from './use-quota';

type CheckEnableCreateElementProps = {
  onSuccess?: () => void;
};

export const useCheckEnableCreateElement = ({ onSuccess }: CheckEnableCreateElementProps = {}) => {
  const t = useTranslations();

  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');

  const { subsPlan, isTeamSpace } = useStore((state) => ({
    subsPlan: state.userInfo?.subs_plan || 'free',
    isTeamSpace: state.isTeamSpace,
  }));

  const { showErrorMsg } = useElementErrorMsg();
  const { elementMonthlyLimit } = useQuota();
  const isNewMembership = useMembershipRevision();
  const hasMaxElementQuota =
    isUltimateOrHigherPlan(subsPlan) || (!isTeamSpace && isNewMembership === true && subsPlan !== 'free');
  const descriptionParams = {
    standardLimit: isNewMembership ? NEW_MEMBERSHIP_REFERENCE_LIMIT : REFERENCE_RIGHT_LIST.standard_v2,
    premiumLimit: isNewMembership ? NEW_MEMBERSHIP_REFERENCE_LIMIT : REFERENCE_RIGHT_LIST.premium_v2,
  };

  const { run: checkCreateElementEnable, runAsync: checkCreateElementEnablePromise } = useFetch(
    checkEnableCreateElement,
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          if (['ElementFreeMonthLimitExceeded', 'ElementPayMonthLimitExceeded'].includes(data.error.reason)) {
            openConfirmDialog(
              isTeamSpace
                ? {
                    title: t('elements.elementDialog.teamLimitTip', {
                      num: elementMonthlyLimit,
                    }),
                    secondaryButtonText: t('elements.checkEnableCreateElement.leftBtn'),
                  }
                : hasMaxElementQuota
                  ? {
                      title: t('elements.checkEnableCreateElement.vip.title'),
                      description: t('elements.checkEnableCreateElement.vip.description', descriptionParams),
                      secondaryButtonText: t('elements.checkEnableCreateElement.vip.leftBtn'),
                      primaryButtonText: t('elements.checkEnableCreateElement.vip.rightBtn'),
                      onPrimaryAction: () => {
                        const feedBackUrl = getFeedBackUrl();
                        openOrRedirect(feedBackUrl);
                      },
                    }
                  : {
                      title: t('elements.checkEnableCreateElement.title'),
                      description: t('elements.checkEnableCreateElement.description', descriptionParams),
                      secondaryButtonText: t('elements.checkEnableCreateElement.leftBtn'),
                      primaryButtonText: t('elements.checkEnableCreateElement.rightBtn'),
                      onPrimaryAction: () => {
                        openPlanDetailDialog();
                        reportEvent('element_click', {
                          element_name: 'upgrade_package',
                        });
                      },
                    },
            );
            if (!hasMaxElementQuota) {
              reportEvent('element_show', {
                element_name: 'upgrade_package',
              });
            }
          } else if (data.error.reason === 'ElementTotalLimitExceeded') {
            openConfirmDialog({
              title: t('elements.checkEnableCreateElement.overTotal.title'),
              secondaryButtonText: t('elements.checkEnableCreateElement.leftBtn'),
            });
          } else {
            showErrorMsg(data.error);
          }
        } else {
          onSuccess?.();
        }
      },
    },
  );

  return {
    checkCreateElementEnable,
    checkCreateElementEnablePromise,
  };
};
